import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { appSettingsService, type AppSetting } from '../../services/app-settings.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { PRETTY } from '../common/ui-kit';

/**
 * Platform-wide numeric settings (App Configuration registry), edited on the
 * screen they tune — eval settings on Evals, conversation settings on Agents.
 * One value for the whole deployment; the allowed range comes from the registry
 * and is enforced again by the server.
 */
const PlatformSettingsDialog = ({ title, keys, visible, onHide, testId }: {
  title: string;
  /** Which registry keys to show, with the label to show them under. */
  keys: { key: string; label: string; unit?: string }[];
  visible: boolean;
  onHide: () => void;
  testId?: string;
}) => {
  const qc = useQueryClient();
  const { data: all = [], isLoading, error } = useQuery({
    queryKey: ['app-settings'], queryFn: appSettingsService.list, enabled: visible, staleTime: 0,
  });
  const [draft, setDraft] = useState<Record<string, number | null>>({});
  const settings = keys.map((k) => ({ ...k, s: all.find((a) => a.key === k.key) }))
    .filter((k): k is typeof k & { s: AppSetting } => !!k.s);

  useEffect(() => {
    if (visible) setDraft(Object.fromEntries(settings.map(({ key, s }) => [key, s.value as number])));
  }, [visible, all]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = settings.filter(({ key, s }) => draft[key] != null && draft[key] !== s.value);
  const save = useMutation({
    mutationFn: async () => {
      for (const { key } of changed) await appSettingsService.update(key, draft[key]);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['app-settings'] }); onHide(); },
  });

  return (
    <Dialog header={title} visible={visible} onHide={() => { save.reset(); onHide(); }}
      style={{ width: '40rem', maxWidth: '95vw' }} dismissableMask data-testid={testId}>
      <p className="text-sm text-ink-muted" style={PRETTY}>
        One value for the whole platform. Changes apply within a minute, without a restart.
      </p>
      {error && <Message severity="error" className="mt-3 w-full" text={extractErrorMessage(error, 'Could not load the settings.')} />}
      {isLoading ? <p className="mt-4 text-sm text-ink-muted">Loading…</p> : (
        <div className="mt-4 flex flex-col gap-4">
          {settings.map(({ key, label, unit, s }) => (
            <div key={key} className="grid grid-cols-1 gap-1 sm:grid-cols-[1fr_11rem] sm:gap-x-4">
              <div className="min-w-0">
                <label htmlFor={key} className="text-sm font-medium text-ink">{label}</label>
                <p className="mt-0.5 text-xs leading-relaxed text-ink-muted" style={PRETTY}>{s.description}</p>
                <p className="mt-1 text-xs text-ink-muted">
                  Default {String(s.defaultValue)}{unit ? ` ${unit}` : ''}
                  {s.min != null && s.max != null ? ` · allowed ${s.min}–${s.max}` : ''}
                  {draft[key] !== s.defaultValue && (
                    <button type="button" className="ui-press ml-2 text-primary underline-offset-2 hover:underline"
                      onClick={() => setDraft((d) => ({ ...d, [key]: s.defaultValue as number }))}>Reset to default</button>
                  )}
                </p>
              </div>
              <InputNumber inputId={key} value={draft[key] ?? null} useGrouping={false} className="w-full self-start"
                min={s.min ?? undefined} max={s.max ?? undefined}
                onValueChange={(e) => setDraft((d) => ({ ...d, [key]: e.value ?? null }))} />
            </div>
          ))}
        </div>
      )}
      {save.isError && <Message severity="error" className="mt-3 w-full" text={extractErrorMessage(save.error, 'Could not save.')} />}
      <div className="mt-5 flex justify-end gap-2">
        <Button label="Cancel" severity="secondary" outlined onClick={() => { save.reset(); onHide(); }} />
        <Button label={changed.length ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}` : 'Save'}
          disabled={!changed.length} loading={save.isPending} onClick={() => save.mutate()}
          data-testid={testId ? `${testId}.Save` : undefined} />
      </div>
    </Dialog>
  );
};

export default PlatformSettingsDialog;
